import { SearchCombobox, type ComboOption } from '@zarya/ui';
import { t } from '@app/i18n';
import { FilterPills } from '@app/main/Identities';
import type { AgentScope, KindResource } from './Resources.api';

/**
 * Filtros de T6 (FR-037): caixas com busca de **tipo**, **ligação**, **dono** e **site**, e a pílula **com tools sem
 * risco**, que liga e desliga. A busca fica na própria `DataTable` (`search`/`onSearchChange`), que já traz o campo e
 * o "Limpar filtros".
 *
 * Filtro de tela é um só desenho no console, não um por tela: a caixa é a de T4 (`SearchCombobox`), e a pílula de
 * ligar e desligar é o `FilterPills` de T4 (aim-pjv, 10/10/2026).
 */

/**
 * `RES_TYPES` do inventário §4.4. As opções são **funções** porque o rótulo vem
 * do catálogo: uma lista de módulo é montada na importação e ficaria presa ao
 * idioma daquele instante.
 */
const kindOptions = (): ComboOption[] => [
  { value: '', label: t('resources.filters.kind.all') },
  { value: 'mcp', label: t('resources.filters.kind.mcp') },
  { value: 'api', label: t('resources.filters.kind.api') },
  { value: 'agent', label: t('resources.filters.kind.agent') },
];

/**
 * Fase 2: a ligação do agente. Escolher uma deixa de fora os MCP servers e as APIs — eles não têm ligação —, e é o
 * servidor quem recorta (`scope`), porque a lista pagina.
 */
const scopeOptions = (): ComboOption[] => [
  { value: '', label: t('resources.filters.scope.all') },
  { value: 'internal', label: t('resources.filters.scope.internal') },
  { value: 'external', label: t('resources.filters.scope.external') },
];

/** A pílula "com tools sem risco" é um filtro de duas posições (FR-032). */
const WITHOUT_RISK = 'sem_risco';
const riskOptions = () => [
  { value: WITHOUT_RISK, label: t('resources.filters.risk.withoutRisk') },
];

export interface ResourcesFiltersProps {
  kind: KindResource | null;
  onTipoChange: (kind: KindResource | null) => void;
  /** Donos conhecidos do catálogo (`getResourcesSummary.owners`). */
  owners: string[];
  owner: string | null;
  onOwnerChange: (owner: string | null) => void;
  semRisco: boolean;
  onSemRiscoChange: (withoutRisk: boolean) => void;
  scope: AgentScope | null;
  onScopeChange: (scope: AgentScope | null) => void;
  /**
   * Slugs dos sites do tenant. **Vazio esconde o combobox** — é a revelação
   * progressiva (P1): quem tem um cluster não ganha um filtro de uma opção só.
   * Quem decide é `multi`, do servidor, na página.
   */
  sites: string[];
  site: string | null;
  onSiteChange: (site: string | null) => void;
}

export function ResourcesFilters({
  kind,
  onTipoChange,
  owners,
  owner,
  onOwnerChange,
  semRisco,
  onSemRiscoChange,
  scope,
  onScopeChange,
  sites,
  site,
  onSiteChange,
}: ResourcesFiltersProps) {
  // A primeira opção volta o filtro a "todos" — o `SearchCombobox` não tem
  // botão de limpar, e um combobox de filtro precisa de caminho de volta.
  const optionsOwner: ComboOption[] = [
    { value: '', label: t('resources.filters.owner.all') },
    ...owners.map((nome) => ({ value: nome, label: nome })),
  ];

  // Mesma forma do combobox de dono: a primeira opção é o caminho de volta para
  // «todos». Sem ela, escolher um site seria uma porta sem maçaneta.
  const siteOptions: ComboOption[] = [
    { value: '', label: t('resources.filters.site.all') },
    ...sites.map((slug) => ({ value: slug, label: slug, mono: true })),
  ];

  return (
    <>
      {/* aim-pjv (decisão do mrolim, 10/10/2026): os filtros de escolha entre opções são caixas com busca. O
          «risco», logo abaixo, liga e desliga, e fica em pílula. */}
      <SearchCombobox
        label={t('resources.filters.kind.label')}
        placeholder={t('resources.filters.kind.placeholder')}
        searchPlaceholder={t('resources.filters.kind.searchPlaceholder')}
        size="sm"
        clearable
        options={kindOptions()}
        value={kind}
        onChange={(valor) => onTipoChange((valor as KindResource | null) || null)}
      />
      <SearchCombobox
        label={t('resources.filters.scope.label')}
        placeholder={t('resources.filters.scope.placeholder')}
        searchPlaceholder={t('resources.filters.scope.searchPlaceholder')}
        size="sm"
        clearable
        options={scopeOptions()}
        value={scope}
        onChange={(valor) => onScopeChange((valor as AgentScope | null) || null)}
      />
      <FilterPills
        label={t('resources.filters.risk.label')}
        options={riskOptions()}
        value={semRisco ? WITHOUT_RISK : null}
        onChange={(valor) => onSemRiscoChange(valor === WITHOUT_RISK)}
      />
      {owners.length > 0 ? (
        <SearchCombobox
          label={t('resources.filters.owner.label')}
          placeholder={t('resources.filters.owner.placeholder')}
          searchPlaceholder={t('resources.filters.owner.searchPlaceholder')}
          size="sm"
          clearable
          options={optionsOwner}
          value={owner}
          onChange={(valor) => onOwnerChange((valor as string | null) || null)}
        />
      ) : null}
      {sites.length > 0 ? (
        <SearchCombobox
          label={t('resources.filters.site.label')}
          placeholder={t('resources.filters.site.placeholder')}
          searchPlaceholder={t('resources.filters.site.searchPlaceholder')}
          size="sm"
          clearable
          options={siteOptions}
          value={site}
          onChange={(valor) => onSiteChange((valor as string | null) || null)}
        />
      ) : null}
    </>
  );
}
